"use client";
/**
 * Badges page (Block 5, design 1.8) + detail bottom sheet.
 *
 * Earned / in-progress / locked grid. Hidden entirely (graceful "not
 * available" state) when badges are disabled or the endpoint 404s.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import { BottomSheet, ProgressBar, SignedOutGate } from "@/components/loyalty/LoyaltyBits";
import {
  useBadges,
  localizedField,
  type BadgeView,
} from "@/hooks/useLoyaltyExpansion";
import { useAuth } from "@/store/auth";

function BadgeTile({
  view,
  onOpen,
  t,
  lang,
}: {
  view: BadgeView;
  onOpen: () => void;
  t: TFunction;
  lang: string;
}) {
  const earned = view.bucket === "earned";
  const locked = view.bucket === "locked";
  const name = locked
    ? "•••"
    : localizedField(view.badge.name, lang) ?? t("v2.badges.untitled");

  return (
    <button
      onClick={onOpen}
      style={{
        padding: 12,
        borderRadius: 14,
        background: "var(--surface-1)",
        boxShadow: earned ? "var(--card-shadow)" : "none",
        border: earned ? "1px solid transparent" : "1px solid var(--border)",
        textAlign: "center",
        position: "relative",
        opacity: locked ? 0.6 : 1,
        cursor: "pointer",
        fontFamily: "inherit",
        width: "100%",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          margin: "0 auto",
          background: earned ? "var(--gold-bg)" : "var(--surface-2)",
          border: `1.5px solid ${earned ? "var(--gold-fg)" : "var(--border)"}33`,
          display: "grid",
          placeItems: "center",
          color: earned ? "var(--gold-fg)" : "var(--ink-3)",
          position: "relative",
          filter: locked ? "grayscale(0.85)" : "none",
        }}
      >
        {locked ? <Icons.lock size={26} /> : <Icons.trophy size={26} />}
        {earned && (
          <div
            style={{
              position: "absolute",
              top: -4,
              insetInlineEnd: -4,
              width: 18,
              height: 18,
              borderRadius: 999,
              background: "var(--primary-600)",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              border: "2px solid var(--surface-1)",
            }}
          >
            <Icons.check size={11} />
          </div>
        )}
      </div>
      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, lineHeight: 1.25, color: "var(--ink-1)" }}>
        {name}
      </div>
      {view.bucket === "in_progress" && view.progress != null && (
        <div style={{ marginTop: 6 }}>
          <ProgressBar value={view.progress} height={4} />
        </div>
      )}
    </button>
  );
}

export function BadgesScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();
  const { loading, error, badgesEnabled, badges, unavailable, refresh } = useBadges();
  const [selected, setSelected] = useState<BadgeView | null>(null);

  const earned = useMemo(() => badges.filter((b) => b.bucket === "earned"), [badges]);
  const inProgress = useMemo(() => badges.filter((b) => b.bucket === "in_progress"), [badges]);
  const locked = useMemo(() => badges.filter((b) => b.bucket === "locked"), [badges]);

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.badges.title")} onBack={() => navigate("/profile")} />
        <SignedOutGate
          icon={<Icons.trophy size={32} />}
          title={t("v2.badges.signin_title")}
          desc={t("v2.badges.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  if (!loading && (unavailable || !badgesEnabled)) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.badges.title")} onBack={() => navigate("/profile")} />
        <div style={{ padding: "42px 24px", textAlign: "center", color: "var(--ink-2)" }}>
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-3)",
              margin: "0 auto 14px",
            }}
          >
            <Icons.trophy size={32} />
          </div>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.badges.unavailable_title")}
          </div>
          <div style={{ maxWidth: 280, margin: "8px auto 0", fontSize: 13, lineHeight: 1.45 }}>
            {t("v2.badges.unavailable_desc")}
          </div>
        </div>
      </div>
    );
  }

  const Section = ({ label, list }: { label: string; list: BadgeView[] }) =>
    list.length > 0 ? (
      <section>
        <div
          className="display"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "var(--ink-2)",
            marginBottom: 10,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {list.map((b) => (
            <BadgeTile key={b.key} view={b} onOpen={() => setSelected(b)} t={t} lang={lang} />
          ))}
        </div>
      </section>
    ) : null;

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.badges.title")}
        onBack={() => navigate("/profile")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "var(--gold-bg)",
              color: "var(--gold-fg)",
              flexShrink: 0,
            }}
          >
            <Icons.trophy size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 15 }}>
              {t("v2.badges.summary", { earned: earned.length, total: badges.length })}
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 1 }}>
              {t("v2.badges.summary_sub")}
            </div>
          </div>
        </Card>

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
          </Card>
        )}

        <Section label={t("v2.badges.earned", { count: earned.length })} list={earned} />
        <Section label={t("v2.badges.in_progress")} list={inProgress} />
        <Section label={t("v2.badges.locked")} list={locked} />

        {!loading && badges.length === 0 && (
          <div style={{ padding: "32px 16px", textAlign: "center" }}>
            <div className="muted" style={{ fontSize: 13 }}>
              {t("v2.badges.empty")}
            </div>
          </div>
        )}
      </div>

      <BadgeDetailSheet view={selected} onClose={() => setSelected(null)} t={t} lang={lang} />
    </div>
  );
}

function BadgeDetailSheet({
  view,
  onClose,
  t,
  lang,
}: {
  view: BadgeView | null;
  onClose: () => void;
  t: TFunction;
  lang: string;
}) {
  const earned = view?.bucket === "earned";
  const locked = view?.bucket === "locked";
  const name = view
    ? locked
      ? t("v2.badges.locked_name")
      : localizedField(view.badge.name, lang) ?? t("v2.badges.untitled")
    : null;
  const desc = view ? localizedField(view.badge.description, lang) : null;

  const shareBadge = async () => {
    if (!view || !name) return;
    const text = t("v2.badges.share_text", { name });
    try {
      if (navigator.share) {
        await navigator.share({ title: name, text });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
    } catch {
      /* user cancelled — no-op */
    }
  };

  return (
    <BottomSheet open={!!view} onClose={onClose} label={name ?? undefined}>
      {view && (
        <div style={{ paddingBottom: 6 }}>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 26,
                margin: "0 auto",
                background: earned ? "var(--gold-bg)" : "var(--surface-2)",
                border: `2px solid ${earned ? "var(--gold-fg)" : "var(--border)"}33`,
                display: "grid",
                placeItems: "center",
                color: earned ? "var(--gold-fg)" : "var(--ink-3)",
                position: "relative",
                filter: locked ? "grayscale(0.85)" : "none",
              }}
            >
              {locked ? <Icons.lock size={42} /> : <Icons.trophy size={42} />}
              {earned && (
                <div
                  style={{
                    position: "absolute",
                    top: -6,
                    insetInlineEnd: -6,
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: "var(--primary-600)",
                    display: "grid",
                    placeItems: "center",
                    color: "#fff",
                    border: "2px solid var(--surface-1)",
                  }}
                >
                  <Icons.check size={14} />
                </div>
              )}
            </div>
            <div className="display" style={{ fontSize: 20, marginTop: 14 }}>
              {name}
            </div>
            {desc && (
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                {desc}
              </div>
            )}
          </div>

          <Card padding={0} style={{ marginTop: 16, boxShadow: "none", border: "1px solid var(--border)" }}>
            {view.badge.awardedAt && (
              <DetailRow
                lab={t("v2.badges.earned_on")}
                val={new Date(view.badge.awardedAt).toLocaleDateString(lang)}
              />
            )}
            {view.progress != null && (
              <>
                {view.badge.awardedAt && <div style={{ height: 1, background: "var(--border)" }} />}
                <DetailRow
                  lab={t("v2.challenges.progress")}
                  val={`${Math.round(view.progress * 100)}%`}
                />
              </>
            )}
            {typeof view.badge.rarityPct === "number" && (
              <>
                <div style={{ height: 1, background: "var(--border)" }} />
                <DetailRow
                  lab={t("v2.badges.rarity")}
                  val={t("v2.badges.rarity_value", {
                    pct: Math.round(view.badge.rarityPct),
                  })}
                />
              </>
            )}
          </Card>

          {earned && (
            <Button full onClick={shareBadge} style={{ marginTop: 18 }} leading={<Icons.share size={16} />}>
              {t("v2.badges.share")}
            </Button>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

function DetailRow({ lab, val }: { lab: string; val: string }) {
  return (
    <div
      style={{
        padding: "12px 14px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 13, color: "var(--ink-2)" }}>{lab}</span>
      <span className="num" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-1)" }}>
        {val}
      </span>
    </div>
  );
}
